/**
 * media-gallery-touch-fix.js
 *
 * Fix: On mobile, page won't scroll when touch starts on product media.
 *
 * Robust approach:
 * - DO NOT stop propagation (which breaks swipe handlers).
 * - Instead, when the gesture is clearly vertical, we "defang" preventDefault()
 *   so theme slider code cannot block native page scrolling.
 * - When gesture is horizontal, we do nothing (theme swipe works normally).
 */

(() => {
  const THRESHOLD_PX = 10;     // wait until user moved a bit
  const VERTICAL_RATIO = 1.4;  // dy must be > dx * ratio to be "vertical"

  let tracking = false;
  let startedInGallery = false;
  let startX = 0;
  let startY = 0;
  let lock = null; // null | 'vertical' | 'horizontal'
  let activePointerId = null;

  function inGallery(target) {
    return !!(target && target.closest && target.closest('media-gallery'));
  }

  function reset() {
    tracking = false;
    startedInGallery = false;
    lock = null;
    startX = 0;
    startY = 0;
    activePointerId = null;
  }

  function decideLock(x, y) {
    const dx = x - startX;
    const dy = y - startY;
    const ax = Math.abs(dx);
    const ay = Math.abs(dy);

    if (ax < THRESHOLD_PX && ay < THRESHOLD_PX) return;

    if (lock === null) {
      if (ay > ax * VERTICAL_RATIO) lock = 'vertical';
      else if (ax > ay * 1.1) lock = 'horizontal';
      // else keep undecided (diagonal) -> don't interfere
    }
  }

  function neutralisePreventDefault(e) {
    // Only matters if the event is cancelable
    if (!e || e.__mgPreventDefaultNeutralised) return;
    e.__mgPreventDefaultNeutralised = true;

    // Replace preventDefault with a no-op for THIS event object.
    try {
      const original = e.preventDefault;
      e.preventDefault = function () {
        // no-op: allow native scrolling
      };
      // Keep a reference just in case anything checks it
      e.__mgOriginalPreventDefault = original;
    } catch (err) {
      // If it fails for some reason, do nothing (worst case: no change)
    }
  }

  // ---------- TOUCH (Safari/iOS + some themes) ----------
  window.addEventListener(
    'touchstart',
    (e) => {
      const t = e.touches && e.touches[0];
      if (!t) return;

      startedInGallery = inGallery(e.target);
      if (!startedInGallery) return;

      tracking = true;
      lock = null;
      startX = t.clientX;
      startY = t.clientY;
    },
    { capture: true, passive: true }
  );

  window.addEventListener(
    'touchmove',
    (e) => {
      if (!tracking || !startedInGallery) return;

      const t = e.touches && e.touches[0];
      if (!t) return;

      decideLock(t.clientX, t.clientY);

      // If clearly vertical: allow page scroll by disabling preventDefault calls
      if (lock === 'vertical') {
        neutralisePreventDefault(e);
      }
      // If horizontal/undecided: do nothing -> theme swipe works
    },
    { capture: true, passive: true }
  );

  window.addEventListener('touchend', reset, { capture: true, passive: true });
  window.addEventListener('touchcancel', reset, { capture: true, passive: true });

  // ---------- POINTER EVENTS (many modern themes use this) ----------
  window.addEventListener(
    'pointerdown',
    (e) => {
      if (e.pointerType && e.pointerType !== 'touch') return;

      startedInGallery = inGallery(e.target);
      if (!startedInGallery) return;

      tracking = true;
      lock = null;
      activePointerId = e.pointerId;
      startX = e.clientX;
      startY = e.clientY;
    },
    { capture: true, passive: true }
  );

  window.addEventListener(
    'pointermove',
    (e) => {
      if (!tracking || !startedInGallery) return;
      if (activePointerId !== null && e.pointerId !== activePointerId) return;
      if (e.pointerType && e.pointerType !== 'touch') return;

      decideLock(e.clientX, e.clientY);

      if (lock === 'vertical') {
        neutralisePreventDefault(e);
      }
    },
    { capture: true, passive: true }
  );

  window.addEventListener('pointerup', reset, { capture: true, passive: true });
  window.addEventListener('pointercancel', reset, { capture: true, passive: true });
})();
